<template>
  <div class="risk-threats mt-3">
    <div class="rfm-anchor rfm-aid-sec-amenazas"></div>

    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-1">
      <h4 class="h5 mb-0">Amenazas</h4>
      <small class="text-muted" v-if="loadedAt">
        {{ tf("risk.last_sync", "Última carga") }}: {{ formatDate(loadedAt) }}
      </small>
    </div>

    <b-alert v-if="loadError" show variant="danger" class="py-1 px-2 mb-2">
      {{ tf("risk.threats_load_error", "No se pudo cargar la sección de amenazas") }}
    </b-alert>

    <div class="rfm-anchor rfm-aid-sub-amenazas-pesos"></div>

    <!-- Weights by catalog -->
    <div v-if="!loading" class="weights-card card mb-2">
      <div class="d-flex align-items-center justify-content-between mb-1">
        <strong>{{ tf("risk.weights_title", "Pesos por catálogo") }}</strong>
        <small class="text-muted">{{ tf("common.total", "Total") }}: {{ weightTotal }}%</small>
      </div>

      <div class="weights-grid">
        <div v-for="catalog in catalogsOrdered" :key="catalog.id" class="weights-item">
          <label class="small mb-1 text-body">{{ catalog.name }}</label>
          <div class="d-flex align-items-center">
            <b-form-input
              type="number"
              min="0"
              max="100"
              step="1"
              class="mr-1"
              :disabled="!canEdit"
              :value="weights[catalog.id]"
              @input="(val) => setWeight(catalog.id, val)"
            />
            <span class="text-body">%</span>
          </div>
        </div>
      </div>

      <small v-if="weightTotal !== 100" class="text-danger">
        {{ tf("risk.weights_must_sum", "Los pesos deben sumar 100%") }}
      </small>
    </div>

    <div v-if="loading" class="text-center my-2">
      <b-spinner label="Loading" variant="primary" />
    </div>

    <div v-else>
      <!-- Sections -->
      <template v-for="section in sectionsOrdered">
        <div
          :key="'anchor-' + section.id"
          :class="['rfm-anchor', 'rfm-aid-sub-amenazas-' + slugify(section.catalog_name)]"
        ></div>

        <div :key="section.id" class="threat-section">
          <div class="d-flex align-items-center justify-content-between mb-1">
            <h5 class="h6 mb-0">{{ section.catalog_name }} — {{ section.name }}</h5>
            <small class="text-muted" v-if="savingAny(section.id)">
              {{ tf("common.saving", "Guardando…") }}
            </small>
          </div>

          <div class="table-responsive">
            <table class="table table-sm table-bordered threats-table">
              <thead class="thead-light">
                <tr>
                  <th style="min-width: 240px">{{ tf("risk.threat", "Amenaza") }}</th>
                  <th style="width: 140px">{{ tf("risk.impact_radius", "Radio de impacto") }}</th>
                  <th style="width: 140px">{{ tf("risk.prob", "Probabilidad") }}</th>
                  <th style="width: 140px">{{ tf("risk.impact", "Impacto") }}</th>
                  <th style="width: 160px">{{ tf("risk.vuln", "Vulnerabilidad") }}</th>
                  <th style="width: 140px">{{ tf("risk.inherent", "Riesgo inherente") }}</th>
                  <th style="width: 160px">{{ tf("risk.residual", "Riesgo residual") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="question in section.questionsOrdered"
                  :key="question.id"
                  :class="{ 'table-row-saving': isSaving(question.id) }"
                >
                  <td>
                    <div class="d-flex align-items-start justify-content-between">
                      <div>
                        <div class="font-weight-600">{{ question.text }}</div>
                        <div class="text-body small">{{ question.code }}</div>
                      </div>
                      <div class="small text-muted" v-if="isSaving(question.id)">…</div>
                    </div>
                  </td>

                  <!-- Radius -->
                  <td>
                    <b-form-select
                      :options="radiusOptions"
                      :value="getAnswerFieldValue(question.id, 'radiusId') || null"
                      size="sm"
                      :disabled="!canEdit"
                      @change="($event) => onFieldChange(question.id, 'radiusId', $event)"
                    />
                  </td>

                  <!-- Probability -->
                  <td>
                    <b-form-select
                      :options="levelOptions"
                      :value="getAnswerFieldValue(question.id, 'probValue') || null"
                      size="sm"
                      :disabled="!canEdit"
                      @change="($event) => onFieldChange(question.id, 'probValue', $event)"
                    />
                  </td>

                  <!-- Impact -->
                  <td>
                    <b-form-select
                      :options="levelOptions"
                      :value="getAnswerFieldValue(question.id, 'impactValue') || null"
                      size="sm"
                      :disabled="!canEdit"
                      @change="($event) => onFieldChange(question.id, 'impactValue', $event)"
                    />
                  </td>

                  <!-- Vulnerability -->
                  <td>
                    <b-form-select
                      :options="levelOptions"
                      :value="getAnswerFieldValue(question.id, 'vulnValue') || null"
                      size="sm"
                      :disabled="!canEdit"
                      @change="($event) => onFieldChange(question.id, 'vulnValue', $event)"
                    />
                  </td>

                  <!-- Inherent -->
                  <td class="text-center align-middle">
                    <b-badge :variant="badgeForLevel(getAnswerFieldValue(question.id, 'inherentValue'))">
                      {{ labelForValue(getAnswerFieldValue(question.id, "inherentValue")) || "—" }}
                    </b-badge>
                  </td>

                  <!-- Residual -->
                  <td class="text-center align-middle">
                    <b-form-select
                      :options="levelOptions"
                      :value="getAnswerFieldValue(question.id, 'residualValue') || null"
                      size="sm"
                      :disabled="!canEdit"
                      @change="($event) => onResidualChange(question.id, $event)"
                    />
                  </td>
                </tr>
              </tbody>

              <tfoot v-if="sectionSummaryViewObj(section.id)">
                <tr class="table-secondary font-weight-600">
                  <td class="text-right">{{ tf("risk.section_avg", "Promedio de la sección") }}</td>
                  <td class="text-center">—</td>
                  <td class="text-center">{{ f2(secAvg(section.id, "avg_probability")) }}</td>
                  <td class="text-center">{{ f2(secAvg(section.id, "avg_impact")) }}</td>
                  <td class="text-center">{{ f2(secAvg(section.id, "avg_vulnerability")) }}</td>
                  <td class="text-center">{{ f2(secAvg(section.id, "avg_inherent")) }}</td>
                  <td class="text-center">{{ f2(secAvg(section.id, "avg_residual")) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </template>

      <div v-if="!sectionsOrdered.length" class="text-muted small">
        {{ tf("risk.threats_empty", "No hay amenazas configuradas") }}
      </div>

      <div class="rfm-anchor rfm-aid-sub-amenazas-resumen"></div>

      <!-- Global summary -->
      <div v-if="globalSummaryView" class="mt-3">
        <h5 class="h6 mb-1">{{ tf("risk.global_summary", "Resumen de Análisis de Amenazas") }}</h5>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>{{ tf("risk.prob", "Probabilidad") }}</th>
                <th>{{ tf("risk.impact", "Impacto") }}</th>
                <th>{{ tf("risk.vuln", "Vulnerabilidad") }}</th>
                <th>{{ tf("risk.inherent", "Riesgo inherente") }}</th>
                <th>{{ tf("risk.residual", "Riesgo residual") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-center">{{ f2(globalSummaryView.avg_probability) }}</td>
                <td class="text-center">{{ f2(globalSummaryView.avg_impact) }}</td>
                <td class="text-center">{{ f2(globalSummaryView.avg_vulnerability) }}</td>
                <td class="text-center">{{ f2(globalSummaryView.avg_inherent) }}</td>
                <td class="text-center">{{ f2(globalSummaryView.avg_residual) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="rfm-anchor rfm-aid-sub-amenazas-resultados"></div>

      <!-- Weighted results -->
      <div v-if="weightedTotalsView" class="mt-2">
        <h5 class="h6 mb-1">
          {{ tf("risk.weighted_results","Resultados del Análisis de Riesgo de la Instalación") }}
        </h5>
        <div class="table-responsive">
          <table class="table table-sm table-bordered">
            <thead class="thead-light">
              <tr>
                <th>{{ tf("risk.weighted_inh", "Promedio ponderado del riesgo inherente") }}</th>
                <th>{{ tf("risk.weighted_res", "Promedio ponderado del riesgo residual") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="text-center">
                  {{ f2(weightedTotalsView.inherent) }} ({{ labelForValue(roundToLevel(weightedTotalsView.inherent)) }})
                </td>
                <td class="text-center">
                  {{ f2(weightedTotalsView.residual) }} ({{ labelForValue(roundToLevel(weightedTotalsView.residual)) }})
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <small class="text-muted">
          {{ tf("risk.weights_note","Nota: los ponderados usan los pesos por catálogo y los promedios por catálogo de cada respuesta.") }}
        </small>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner, BAlert, BFormSelect, BBadge, BFormInput } from "bootstrap-vue";
import axios from "@axios";

export default {
  name: "RiskThreatPart",
  components: { BSpinner, BAlert, BFormSelect, BBadge, BFormInput },
  props: {
    riskEvaluationId: { type: [Number, String], required: true },
    deferSave: { type: Boolean, default: true },
    autosaveMode: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: true,
      loadError: false,
      loadedAt: null,

      sections: [],
      levels: [],
      radius: [],
      catalogs: [],

      answers: {},
      saving: {},

      sectionSummary: {},
      globalSummary: null,
      weightedTotals: null,

      liveSectionSummary: {},
      liveGlobalSummary: null,
      liveWeightedTotals: null,

      weights: {},
      weightsOriginal: {},
      weightsDirty: false,
      weightsDirtyById: {},

      dirtyRows: {},
      questionToSection: {},
      sectionToCatalogId: {},
    };
  },
  computed: {
    sectionsOrdered() {
      const sortedSections = this.sortByDisplayOrder(this.sections);
      return sortedSections.map((sectionItem) => ({
        ...sectionItem,
        questionsOrdered: this.sortByDisplayOrder(sectionItem.questions || []),
      }));
    },
    catalogsOrdered() {
      return this.sortByDisplayOrder(this.catalogs);
    },
    levelOptions() {
      const base = [{ value: null, text: this.tf("common.select", "Seleccione…") }];
      const items = this.sortByDisplayOrder(this.levels).map((l) => ({
        value: l.value,
        text: `${l.label} (${l.value})`,
      }));
      return base.concat(items);
    },
    radiusOptions() {
      const base = [{ value: null, text: this.tf("common.select", "Seleccione…") }];
      const items = this.sortByDisplayOrder(this.radius).map((r) => ({ value: r.id, text: r.label }));
      return base.concat(items);
    },
    weightTotal() {
      return Object.values(this.weights).reduce((sum, v) => sum + (isNaN(Number(v)) ? 0 : Number(v)), 0);
    },
    globalSummaryView() {
      return this.liveGlobalSummary || this.globalSummary;
    },
    weightedTotalsView() {
      return this.liveWeightedTotals || this.weightedTotals;
    },
    _weightsLsKey() {
      return `rfm:weights:${this.riskEvaluationId}`;
    },
  },
  async mounted() {
    await this.loadAll();
  },
  watch: {
    riskEvaluationId() {
      this.loadAll();
    },
  },
  methods: {
    //--- Utils ----
    slugify(s) {
      return String(s || "")
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
    },
    sortByDisplayOrder(arr) {
      const a = Array.isArray(arr) ? arr.slice() : [];
      a.sort((l, r) => (l.display_order || 0) - (r.display_order || 0));
      return a;
    },
    apiUrl(suffix) {
      return "/v1/risk_evaluations/" + this.riskEvaluationId + (suffix || "");
    },
    tf(key, fallback) {
      try {
        const out = this.$t ? this.$t(key) : key;
        return typeof out === "string" && out === key ? fallback || "" : out;
      } catch {
        return fallback || "";
      }
    },
    f2(n) {
      return n == null || isNaN(n) ? "—" : Number(n).toFixed(2);
    },
    getAnswerFieldValue(qid, field) {
      const row = this.answers[qid] || null;
      return row ? row[field] : null;
    },
    roundToLevel(n) {
      if (n == null || isNaN(n)) return null;
      let x = Math.round(Number(n));
      return Math.min(5, Math.max(1, x));
    },
    labelForValue(v) {
      const n = Number(v);
      if (isNaN(n)) return null;
      for (const L of this.levels) if (Number(L.value) === n) return `${L.label} (${n})`;
      return null;
    },
    badgeForLevel(v) {
      const map = { 1: "light", 2: "secondary", 3: "info", 4: "warning", 5: "danger" };
      return map[Number(v)] || "secondary";
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString();
      } catch {
        return iso;
      }
    },

    //--- Load -----
    async loadAll() {
      this.loading = true;
      this.loadError = false;
      try {
        const { data } = await axios.get(this.apiUrl("/threats"));
        const payload = data?.data || data || {};

        this.sections = Array.isArray(payload.sections) ? payload.sections : [];
        this.levels = Array.isArray(payload.levels) ? payload.levels : [];
        this.radius = Array.isArray(payload.radius) ? payload.radius : [];
        this.catalogs = Array.isArray(payload.catalogs) ? payload.catalogs : [];

        this.buildLookups();

        // weights (from server)
        const w = {};
        for (const c of this.catalogs) w[c.id] = Number(c.weight_pct || 0);
        this.weights = { ...w };

        // local fallback: if the server returns 0 for a catalog and there is a non-zero local snapshot we use it
        this.tryRestoreLocalWeightsFallback();

        this.weightsOriginal = { ...this.weights };
        this.weightsDirty = false;
        this.weightsDirtyById = {};

        // answers
        const A = {};
        const incoming = Array.isArray(payload.answers) ? payload.answers : [];
        for (const ans of incoming) {
          const qid = Number(ans.id_risk_threat_question);
          const inherent = ans.inherent_score || this.computeInherent(ans.prob_value, ans.impact_value, ans.vuln_value);
          A[qid] = {
            radiusId: ans.id_risk_impact_radius_catalog || null,
            probValue: ans.prob_value || null,
            impactValue: ans.impact_value || null,
            vulnValue: ans.vuln_value || null,
            inherentValue: inherent || null,
            residualValue: (ans.residual_score != null ? ans.residual_score : inherent) || null,
          };
        }
        for (const s of this.sections) {
          for (const q of s.questions || []) {
            if (!A[q.id])
              A[q.id] = { radiusId: null, probValue: null, impactValue: null, vulnValue: null, inherentValue: null, residualValue: null };
          }
        }
        this.answers = A;
        this.dirtyRows = {};
        this.loadedAt = new Date
        await this.loadSummaries();
        await this.$nextTick();
        this.recomputeLiveSummaries();

        this._emit("changed", { reason: "loadAll" });
      } catch (e) {
        console.error("RiskThreatPart loadAll error", e);
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },

    async loadSummaries() {
      try {
        const { data } = await axios.get(this.apiUrl("/threats/summary"));
        const payload = data?.data || data || {};
        const map = {};
        for (const row of payload.sections || []) map[row.id_risk_threat_section] = row;
        this.sectionSummary = map;
        this.globalSummary = payload.global || null;
        this.weightedTotals = payload.weighted || null;

        // refresh weights from summary (only if there are no local dirties)
        if (Array.isArray(payload.catalogs)) {
          for (const c of payload.catalogs) {
            if (this.weightsDirtyById?.[c.id]) continue;
            this.$set(this.weights, c.id, Number(c.weight_pct || 0));
          }
          this.tryRestoreLocalWeightsFallback(); // keep fallback if there are still zeros
          this.weightsOriginal = { ...this.weights };
          this.weightsDirty = Object.keys(this.weightsDirtyById || {}).length > 0;
        }
      } catch (e) {
        console.log("loadSummaries error", e);
      }
    },

    // --- lookups AND calculations ----
    buildLookups() {
      const q2s = {},
        s2c = {};
      for (const s of this.sections) {
        const secId = s.id;
        let catId = s.id_risk_threat_catalog || s.catalog_id || null;
        if (!catId && s.catalog_name) {
          const name = String(s.catalog_name).toLowerCase();
          for (const c of this.catalogs) if (String(c.name).toLowerCase() === name) { catId = c.id; break; }
        }
        s2c[secId] = catId || null;
        for (const q of s.questions || []) q2s[q.id] = secId;
      }
      this.sectionToCatalogId = s2c;
      this.questionToSection = q2s;
    },
    recomputeLiveSummaries() {
      this.liveSectionSummary = this.computeSectionAverages();
      this.liveGlobalSummary = this.computeGlobalAverages(this.liveSectionSummary);
      this.liveWeightedTotals = this.computeWeightedTotals(this.liveSectionSummary);
    },
    computeSectionAverages() {
      const out = {};
      for (const s of this.sections) {
        const sid = s.id,
          qs = Array.isArray(s.questions) ? s.questions : [];
        const P = [],
          I = [],
          V = [],
          INH = [],
          RES = [];
        for (const q of qs) {
          const row = this.answers[q.id];
          if (!row) continue;
          const pv = Number(row.probValue);
          if (!isNaN(pv) && pv > 0) P.push(pv);
          const iv = Number(row.impactValue);
          if (!isNaN(iv) && iv > 0) I.push(iv);
          const vv = Number(row.vulnValue);
          if (!isNaN(vv) && vv > 0) V.push(vv);
          const ih = Number(row.inherentValue);
          if (!isNaN(ih) && ih > 0) INH.push(ih);
          const rv = Number(row.residualValue);
          if (!isNaN(rv) && rv > 0) RES.push(rv);
        }
        out[sid] = {
          id_risk_threat_section: sid,
          section_weight_pct: Number(this.sectionToCatalogWeight(sid)),
          avg_probability: this.avg(P),
          avg_impact: this.avg(I),
          avg_vulnerability: this.avg(V),
          avg_inherent: this.avg(INH),
          avg_residual: this.avg(RES),
          avg_overall: this.avg(INH),
        };
      }
      return out;
    },
    avg(arr) {
      if (!arr || !arr.length) return null;
      const s = arr.reduce((a, b) => a + Number(b || 0), 0);
      const m = s / arr.length;
      return isNaN(m) ? null : m;
    },
    sectionToCatalogWeight(sid) {
      const cid = this.sectionToCatalogId[sid] || null;
      if (!cid) return 0;
      const w = Number(this.weights[cid] || 0);
      return isNaN(w) ? 0 : w;
    },
    computeGlobalAverages(secMap) {
      const keys = ["avg_probability", "avg_impact", "avg_vulnerability", "avg_inherent", "avg_residual"];
      const sums = {},
        counts = {};
      keys.forEach((k) => {
        sums[k] = 0;
        counts[k] = 0;
      });
      for (const sid in secMap) {
        const row = secMap[sid];
        for (const k of keys) {
          const v = row[k];
          if (v != null && !isNaN(v)) {
            sums[k] += Number(v);
            counts[k] += 1;
          }
        }
      }
      const res = {};
      for (const k of keys) res[k] = counts[k] > 0 ? sums[k] / counts[k] : null;
      return { id: null, ...res };
    },
    computeWeightedTotals(secMap) {
      const byCat = {};
      for (const sid in secMap) {
        const cid = this.sectionToCatalogId[Number(sid)] || null;
        if (!cid) continue;
        if (!byCat[cid]) byCat[cid] = { inh: [], res: [] };
        const r = secMap[sid];
        if (r.avg_inherent != null && !isNaN(r.avg_inherent)) byCat[cid].inh.push(Number(r.avg_inherent));
        if (r.avg_residual != null && !isNaN(r.avg_residual)) byCat[cid].res.push(Number(r.avg_residual));
      }
      let inh = 0,
        res = 0,
        wsum = 0;
      for (const cid in byCat) {
        const lists = byCat[cid],
          aI = this.avg(lists.inh),
          aR = this.avg(lists.res);
        const w = Number(this.weights[cid] || 0) || 0;
        if (aI != null && !isNaN(aI)) inh += (aI * w) / 100.0;
        if (aR != null && !isNaN(aR)) res += (aR * w) / 100.0;
        wsum += w;
      }
      return { sum_weights: wsum, inherent: inh || null, residual: res || null };
    },

    // ---------- UI changes ----------
    setWeight(catalogId, val) {
      if (!this.canEdit) return;

      const numericInput = String(val).trim() === "" ? 0 : Number(val);
      const newValue = isNaN(numericInput) ? 0 : numericInput;

      this.$set(this.weights, catalogId, newValue);
      this.weightsDirty = true;
      if (!this.weightsDirtyById) this.weightsDirtyById = {};
      this.$set(this.weightsDirtyById, Number(catalogId), true);

      // immediate local snapshot (in case the server has not persisted yet)
      this.persistLocalWeightsSnapshot();

      this.recomputeLiveSummaries();
      this.$emit("weights-changed", { id: Number(catalogId), value: newValue });
    },

    onFieldChange(qid, field, value) {
      if (!this.canEdit) return;

      const cur = this.answers[qid] || {};
      const upd = { ...cur, [field]: value === "" ? null : value };

      upd.inherentValue = this.computeInherent(upd.probValue, upd.impactValue, upd.vulnValue);
      upd.residualValue = upd.inherentValue || null;

      this.$set(this.answers, qid, upd);
      this.$set(this.dirtyRows, qid, true);
      this.recomputeLiveSummaries();

      this._emit("answers-changed", { qid, field, value: upd[field] });
      this._emit("dirty", { type: "answer", qid });
      this._emit("input", { scope: "threats.answers", qid, field });
      this._emit("changed", { scope: "threats.answers", qid, field });
    },

    onResidualChange(qid, value) {
      if (!this.canEdit) return;

      const cur = this.answers[qid] || {};
      const upd = { ...cur, residualValue: value === "" ? null : Number(value) };
      this.$set(this.answers, qid, upd);
      this.$set(this.dirtyRows, qid, true);
      this.recomputeLiveSummaries();

      this._emit("answers-changed", { qid, field: "residualValue", value: upd.residualValue });
      this._emit("dirty", { type: "answer", qid });
      this._emit("input", { scope: "threats.answers", qid, field: "residualValue" });
      this._emit("changed", { scope: "threats.answers", qid, field: "residualValue" });
    },

    computeInherent(p, i, v) {
      const P = Number(p || 0),
        I = Number(i || 0),
        V = Number(v || 0);
      if (!P || !I || !V) return null;
      const average = (P + I + V) / 3;
      const raw = Math.round(average);
      return Math.min(5, Math.max(1, raw));
    },

    // ---------- state ----------
    isRowReady(row) {
      return row && row.radiusId && row.probValue && row.impactValue && row.vulnValue && row.inherentValue && row.residualValue;
    },
    isSaving(qid) {
      return !!this.saving[qid];
    },
    savingAny(sectionId) {
      const sec = this.sections.find((s) => s.id === sectionId);
      if (!sec) return false;
      return (sec.questions || []).some((q) => this.isSaving(q.id));
    },
    isBusy() {
  return !!this.loading || Object.keys(this.saving || {}).length > 0;
},


    // ---------- public API ----------
    hasDirty() {
      return this.weightsDirty || Object.keys(this.dirtyRows).length > 0;
    },
    validateBeforeSave() {
      if (this.weightTotal !== 100) return { ok: false, message: this.tf("risk.weights_must_sum", "Los pesos deben sumar 100%") };
      for (const qid of Object.keys(this.dirtyRows))
        if (!this.isRowReady(this.answers[qid])) {
          return { ok: false, message: this.tf("risk.fill_all_fields", "Completa todos los campos de la fila antes de guardar") };
        }
      return { ok: true, message: "OK" };
    },
    validateBeforeReview() {
      if (this.weightTotal !== 100) return { ok: false, message: this.tf("risk.weights_must_sum", "Los pesos deben sumar 100%") };
      for (const s of this.sections) {
        for (const q of s.questions || []) {
          if (!this.isRowReady(this.answers[q.id])) return { ok: false, message: this.tf("risk.threats_missing", "Faltan pesos o respuestas en Amenazas") };
        }
      }
      return { ok: true, message: "OK" };
    },

    // ----- WEIGHTS -----
    async saveWeights({ allowPartial = true, silent = true } = {}) {
      if (!this.canEdit) return;

      const dirtyIds = Object.keys(this.weightsDirtyById || {}).filter((k) => !!this.weightsDirtyById[k]);
      if (!dirtyIds.length) return;

      const payload = dirtyIds.map((k) => ({
        id_risk_threat_catalog: Number(k),
        id_catalog: Number(k), //backend expects this key
        weight_pct: Number(this.weights[k] || 0),
        id_risk_evaluation: Number(this.riskEvaluationId), //explicit the backend requires it
      }));

      try {
        await axios.post(
          this.apiUrl("/threats/weights"),
          { weights: payload },
          {
            params: allowPartial ? { partial: 1 } : {},
            headers: allowPartial ? { "X-Skip-Weights-Validation": "1", "X-Requested-From": "autosave" } : {},
          }
        );

        // After saving, confirm again with the server (avoids opening with 0)
        await this.refreshWeightsFromServer();

        // local snapshot (last known)
        this.persistLocalWeightsSnapshot();

        if (!silent) this.$bvToast?.toast(this.tf("risk.weights_saved", "Pesos guardados"), { variant: "success" });
      } catch (e) {
        if (!silent) this.$bvToast?.toast(this.tf("risk.weights_save_failed", "No se pudieron guardar los pesos"), { variant: "danger" });
        console.log("[Threats] saveWeights error", e?.response || e);
      }
    },

    async refreshWeightsFromServer() {
      try {
        const res = await axios.get(this.apiUrl("/threats/summary"), { params: { _ts: Date.now() } });
        const data = res?.data?.data || res?.data || {};
        if (Array.isArray(data.catalogs)) {
          const server = {};
          data.catalogs.forEach((c) => {
            server[c.id] = Number(c.weight_pct || 0);
          });
          // if there are local dirties, clear only the ones that actually arrived from the server
          for (const idStr of Object.keys(this.weightsDirtyById || {})) {
            const id = Number(idStr);
            if (server[id] != null) this.$delete(this.weightsDirtyById, idStr);
          }
          this.weightsDirty = Object.keys(this.weightsDirtyById || {}).length > 0;

          // server wins, but if there are still zeros and we have a local fallback, apply it
          this.weights = { ...server };
          this.tryRestoreLocalWeightsFallback();
          this.weightsOriginal = { ...this.weights };
        }
      } catch (e) {
        console.log("[Threats] refreshWeightsFromServer error", e?.response || e);
      }
    },

    persistLocalWeightsSnapshot() {
      try {
        const snapshot = { savedAt: new Date().toISOString(), weights: this.weights };
        localStorage.setItem(this._weightsLsKey, JSON.stringify(snapshot));
      } catch {}
    },
    tryRestoreLocalWeightsFallback() {
      try {
        const raw = localStorage.getItem(this._weightsLsKey);
        if (!raw) return;
        const snap = JSON.parse(raw);
        const localW = snap?.weights || {};
        // if the server has 0 in any catalog and local had >0, use local ONLY for that catalog
        let changed = false;
        for (const c of this.catalogs) {
          const id = Number(c.id);
          const serverVal = Number(this.weights[id] || 0);
          const localVal = Number(localW[id] || 0);
          if (serverVal === 0 && localVal > 0) {
            this.$set(this.weights, id, localVal);
            changed = true;
          }
        }
        if (changed) {
          this.weightsDirty = true;
          for (const id in this.weights) {
            if (Number(this.weights[id]) !== Number(this.weightsOriginal[id] || 0)) {
              this.$set(this.weightsDirtyById, Number(id), true);
            }
          }
        }
      } catch {}
    },

    // ----- ANSWERS -----
    async saveAnswersOnly({ allowPartial = true, skipWeightValidation = true, skipEmptyValidation = true, silent = true } = {}) {
      if (!this.canEdit) return;

      const dirtyQuestionIds = Object.keys(this.dirtyRows || {});
      if (!dirtyQuestionIds.length) return;

      for (const qidStr of dirtyQuestionIds) {
        const questionId = Number(qidStr);
        const answerRow = this.answers[questionId] || {};

        if (!skipEmptyValidation && !this.isRowReady(answerRow)) continue;
       // if (skipEmptyValidation && !this.isRowReady(answerRow)) continue;

        this.$set(this.saving, questionId, true);
        try {
          await axios.post(
            this.apiUrl("/threats/answers"),
            {
              id_risk_evaluation: Number(this.riskEvaluationId),
              id_risk_threat_question: questionId,
              id_risk_impact_radius_catalog: answerRow.radiusId,
              prob_value: Number(answerRow.probValue),
              impact_value: Number(answerRow.impactValue),
              vuln_value: Number(answerRow.vulnValue),
              inherent_score: Number(answerRow.inherentValue),
              residual_score: Number(answerRow.residualValue),
            },
            {
              params: allowPartial ? { partial: 1 } : {},
              headers: {
                ...(allowPartial ? { "X-Requested-From": "autosave" } : {}),
                ...(skipWeightValidation ? { "X-Skip-Weights-Validation": "1" } : {}),
                ...(skipEmptyValidation ? { "X-Skip-Empty-Validation": "1" } : {}),
              },
            }
          );
          this.$delete(this.dirtyRows, questionId);
          if (!silent) this.$emit("answers-changed", { id: questionId });
        } catch (e) {
          console.log("[Threats] saveAnswersOnly error", e?.response || e);
        } finally {
          this.$delete(this.saving, questionId);
        }
      }

      this.recomputeLiveSummaries();
    },

    async saveSectionsOnly() {
      return this.saveAnswersOnly();
    },

    async saveSummary({ allowPartial = true, silent = true } = {}) {
      try {
        const res = await axios.get(this.apiUrl("/threats/summary"), {
          params: allowPartial ? { partial: 1, _ts: Date.now() } : { _ts: Date.now() },
          headers: allowPartial ? { "X-Requested-From": "autosave" } : {},
        });
        const payload = res?.data?.data || res?.data || {};
        const sectionSummaryMap = {};
        const sectionArray = Array.isArray(payload.sections) ? payload.sections : [];
        for (const row of sectionArray) sectionSummaryMap[row.id_risk_threat_section] = row;

        this.sectionSummary = sectionSummaryMap;
        this.globalSummary = payload.global || null;
        this.weightedTotals = payload.weighted || null;

        if (Array.isArray(payload.catalogs)) {
          for (const cat of payload.catalogs) {
            const id = Number(cat.id);
            if (this.weightsDirtyById?.[id]) continue;
            this.$set(this.weights, id, Number(cat.weight_pct || 0));
          }
          this.tryRestoreLocalWeightsFallback();
          this.weightsOriginal = { ...this.weights };
          this.weightsDirty = Object.keys(this.weightsDirtyById || {}).length > 0;
        }

        this.recomputeLiveSummaries();
      } catch (e) {
        console.log("[Threats] saveSummary error", e?.response || e);
      }
    },

    async saveAll() {
      if (!this.canEdit) return;

      await this.saveWeights();
      await this.saveAnswersOnly();
      await this.loadSummaries();
      this.recomputeLiveSummaries();
    },

    // ---------- helpers ----------
    sectionSummaryViewObj(sectionId) {
      const live = this.liveSectionSummary && this.liveSectionSummary[sectionId];
      return live || (this.sectionSummary ? this.sectionSummary[sectionId] || null : null);
    },
    secAvg(sectionId, key) {
      const o = this.sectionSummaryViewObj(sectionId);
      if (!o) return null;
      const v = o[key];
      return v == null || isNaN(v) ? null : Number(v);
    },

    _emit(name, payload) {
      console.log(`[RFM][emit][threats] ${name}`, payload || {});
      this.$emit(name, payload);
    },
  },
};
</script>

<style scoped>
.risk-threats .threat-section + .threat-section { margin-top: 12px; }
.threats-table th, .threats-table td { vertical-align: middle; }
.font-weight-600 { font-weight: 600; }
.table-row-saving { opacity: 0.7; }
.weights-card { background:#f9fafb; border:1px solid #eceff3; border-radius:8px; padding:10px 12px; }
.weights-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(220px, 1fr)); gap:8px; }
.weights-item { background:#fff; border:1px solid #eceff3; border-radius:8px; padding:8px; }
.rfm-anchor { position:relative; height:1px; margin-top:-4px; }
</style>

<style>
.dark-layout .weights-item {
  background: var(--third-dark) !important;
  border-color: var(--second-dark) !important;
}
</style>
